import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Button,
  Typography,
  Chip,
  Pagination,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import registrationService from "../../services/registrationService";
import departmentService from "../../services/departmentService";

/**
 * Formats a date string to IST (UTC+05:30) display.
 */
const formatToIST = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const PAGE_LIMIT = 50;

const ApproveRegistration = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Pending tab state
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState(null);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState(null); // { id, action }

  // History tab state
  const [historyRequests, setHistoryRequests] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);

  // Email search state
  const [pendingEmailSearch, setPendingEmailSearch] = useState("");
  const [historyEmailSearch, setHistoryEmailSearch] = useState("");
  const pendingDebounceRef = useRef(null);
  const historyDebounceRef = useRef(null);

  // View formData dialog state
  const [viewRecord, setViewRecord] = useState(null);

  // Filter state
  const [departments, setDepartments] = useState([]);
  const [pendingDeptFilter, setPendingDeptFilter] = useState("");
  const [historyDeptFilter, setHistoryDeptFilter] = useState("");
  const [historyStatusFilter, setHistoryStatusFilter] = useState("");

  const fetchPending = useCallback(async (page = 1, emailSearch = "", deptFilter = "") => {
    setPendingLoading(true);
    setPendingError(null);
    try {
      const params = { page, limit: PAGE_LIMIT };
      if (emailSearch.trim()) {
        params.emailSearch = emailSearch.trim();
      }
      if (deptFilter) {
        params.departmentId = deptFilter;
      }
      const res = await registrationService.listPending(params);
      const data = res?.data?.data ?? res?.data ?? {};
      setPendingRequests(data.rows ?? data.requests ?? []);
      setPendingTotal(data.total ?? data.count ?? 0);
    } catch (err) {
      setPendingError(
        err?.response?.data?.message || "Failed to load pending requests."
      );
      setPendingRequests([]);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (page = 1, emailSearch = "", deptFilter = "", statusFilter = "") => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const params = { page, limit: PAGE_LIMIT };
      if (emailSearch.trim()) {
        params.emailSearch = emailSearch.trim();
      }
      if (deptFilter) {
        params.departmentId = deptFilter;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }
      const res = await registrationService.listHistory(params);
      const data = res?.data?.data ?? res?.data ?? {};
      setHistoryRequests(data.rows ?? data.requests ?? []);
      setHistoryTotal(data.total ?? data.count ?? 0);
    } catch (err) {
      setHistoryError(
        err?.response?.data?.message || "Failed to load decision history."
      );
      setHistoryRequests([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Fetch data on tab or page change
  useEffect(() => {
    if (activeTab === 0) {
      fetchPending(pendingPage, pendingEmailSearch, pendingDeptFilter);
    }
  }, [activeTab, pendingPage, pendingEmailSearch, pendingDeptFilter, fetchPending]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchHistory(historyPage, historyEmailSearch, historyDeptFilter, historyStatusFilter);
    }
  }, [activeTab, historyPage, historyEmailSearch, historyDeptFilter, historyStatusFilter, fetchHistory]);

  // Fetch departments for filter dropdowns
  useEffect(() => {
    departmentService
      .getDepartmentNames()
      .then((res) => {
        const data = res?.data?.data ?? res?.data ?? [];
        setDepartments(Array.isArray(data) ? data : []);
      })
      .catch(() => setDepartments([]));
  }, []);

  const handleDecision = async (requestId, action) => {
    setActionLoading({ id: requestId, action });
    try {
      await registrationService.decide(requestId, { action });
      // Refresh pending list after decision
      await fetchPending(pendingPage, pendingEmailSearch, pendingDeptFilter);
    } catch (err) {
      setPendingError(
        err?.response?.data?.message || `Failed to ${action} request.`
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePendingPageChange = (_event, page) => {
    setPendingPage(page);
  };

  const handleHistoryPageChange = (_event, page) => {
    setHistoryPage(page);
  };

  const handlePendingSearchChange = (event) => {
    const value = event.target.value;
    if (pendingDebounceRef.current) {
      clearTimeout(pendingDebounceRef.current);
    }
    pendingDebounceRef.current = setTimeout(() => {
      setPendingEmailSearch(value);
      setPendingPage(1);
    }, 400);
  };

  const handleHistorySearchChange = (event) => {
    const value = event.target.value;
    if (historyDebounceRef.current) {
      clearTimeout(historyDebounceRef.current);
    }
    historyDebounceRef.current = setTimeout(() => {
      setHistoryEmailSearch(value);
      setHistoryPage(1);
    }, 400);
  };

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      if (pendingDebounceRef.current) clearTimeout(pendingDebounceRef.current);
      if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
    };
  }, []);

  const pendingPageCount = Math.ceil(pendingTotal / PAGE_LIMIT);
  const historyPageCount = Math.ceil(historyTotal / PAGE_LIMIT);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Approve Registration
      </Typography>

      <Paper sx={{ mt: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
        >
          <Tab label="Pending Requests" />
          <Tab label="Decision History" />
        </Tabs>

        {/* Pending Requests Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 2 }}>
            {pendingError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPendingError(null)}>
                {pendingError}
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search by email..."
                onChange={handlePendingSearchChange}
                sx={{ width: { xs: "100%", sm: 300 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="pending-dept-filter-label">Department</InputLabel>
                <Select
                  labelId="pending-dept-filter-label"
                  value={pendingDeptFilter}
                  label="Department"
                  onChange={(e) => { setPendingDeptFilter(e.target.value); setPendingPage(1); }}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {pendingLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : pendingRequests.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography color="text.secondary">
                  No pending requests
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>PRN</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Class</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Submitted At</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">View</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingRequests.map((request) => (
                        <TableRow key={request.id} hover>
                          <TableCell>{request.prn || "—"}</TableCell>
                          <TableCell>{request.email || "—"}</TableCell>
                          <TableCell>
                            {request.department?.name || request.departmentName || "—"}
                          </TableCell>
                          <TableCell>
                            {request.class ? (request.class.section ? `${request.class.name} - ${request.class.section}` : request.class.name) : (request.className || "—")}
                          </TableCell>
                          <TableCell>{formatToIST(request.submittedAt || request.createdAt)}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => setViewRecord(request)}
                              aria-label={`View details for ${request.prn}`}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                disabled={actionLoading !== null}
                                onClick={() => handleDecision(request.id, "approve")}
                              >
                                {actionLoading?.id === request.id && actionLoading?.action === "approve" ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  "Approve"
                                )}
                              </Button>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                disabled={actionLoading !== null}
                                onClick={() => handleDecision(request.id, "reject")}
                              >
                                {actionLoading?.id === request.id && actionLoading?.action === "reject" ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  "Reject"
                                )}
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {pendingPageCount > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Pagination
                      count={pendingPageCount}
                      page={pendingPage}
                      onChange={handlePendingPageChange}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* Decision History Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 2 }}>
            {historyError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHistoryError(null)}>
                {historyError}
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search by email..."
                onChange={handleHistorySearchChange}
                sx={{ width: { xs: "100%", sm: 300 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="history-dept-filter-label">Department</InputLabel>
                <Select
                  labelId="history-dept-filter-label"
                  value={historyDeptFilter}
                  label="Department"
                  onChange={(e) => { setHistoryDeptFilter(e.target.value); setHistoryPage(1); }}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="history-status-filter-label">Status</InputLabel>
                <Select
                  labelId="history-status-filter-label"
                  value={historyStatusFilter}
                  label="Status"
                  onChange={(e) => { setHistoryStatusFilter(e.target.value); setHistoryPage(1); }}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {historyLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : historyRequests.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography color="text.secondary">
                  No decision history
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>PRN</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Decided At</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">View</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historyRequests.map((request) => (
                        <TableRow key={request.id} hover>
                          <TableCell>{request.prn || "—"}</TableCell>
                          <TableCell>{request.email || "—"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={request.status === "approved" ? "Approved" : "Rejected"}
                              color={request.status === "approved" ? "success" : "error"}
                            />
                          </TableCell>
                          <TableCell>{formatToIST(request.decisionAt || request.updatedAt)}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => setViewRecord(request)}
                              aria-label={`View details for ${request.prn}`}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {historyPageCount > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Pagination
                      count={historyPageCount}
                      page={historyPage}
                      onChange={handleHistoryPageChange}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* View Student Details Dialog */}
      <Dialog open={!!viewRecord} onClose={() => setViewRecord(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Student Details — {viewRecord?.prn}</DialogTitle>
        <DialogContent dividers>
          {viewRecord?.formData && Object.entries(viewRecord.formData).map(([key, value]) => (
            value && key !== "syncedFromExisting" && (
              <Box key={key} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                  {key.replace(/([A-Z])/g, " $1").trim()}
                </Typography>
                <Typography variant="body2">{String(value)}</Typography>
              </Box>
            )
          ))}
          {!viewRecord?.formData && <Typography>No form data available</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewRecord(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApproveRegistration;
